import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { useDirectory, useUpdateMemberRole } from '../hooks/useDirectory';
import { DirectoryMember, DirectoryFilters, MemberRelationship } from '../types';
import { 
  Card, 
  Badge, 
  Button, 
  Avatar, 
  Spinner, 
  Input,
  ErrorBanner 
} from '../components/ui';
import { 
  Search, 
  Filter, 
  MessageSquare, 
  ChevronRight, 
  X, 
  Globe, 
  Calendar, 
  Tag, 
  Info,
  User,
  MapPin,
  LayoutList,
  ShieldCheck
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn, safeFormat } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import debounce from 'lodash.debounce';

import { useCreateDirectChannel } from '../hooks/useChat';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';

// --- Subcomponents ---

const MemberDrawer: React.FC<{ 
  member: DirectoryMember | null; 
  relationships: MemberRelationship[];
  allMembers: DirectoryMember[];
  onClose: () => void;
  onUpdateRole?: (id: string, role: string) => void;
}> = ({ member, relationships, allMembers, onClose, onUpdateRole }) => {
  const { node, getTerm } = useTenant();
  const { user } = useCurrentUser();
  const perms = usePermissions();
  const navigate = useNavigate();
  const createDirectChannel = useCreateDirectChannel(node?.id || '');
  const [isEditingRole, setIsEditingRole] = React.useState(false);
  const [selectedRole, setSelectedRole] = React.useState(member?.role || 'MEMBER');

  const memberTerm = getTerm('member');

  if (!member) return null;

  const canManage = perms.isAdmin || perms.can('ROLE_MANAGE') || perms.can('manage_members');
  const isUpstream = member.nodeId !== node?.id;
  const currentUserId = user?.id || user?.userId;
  const isCurrentUser = Boolean(currentUserId && member.userId === currentUserId);
  const allowDirectMessage = !isCurrentUser && (!isUpstream || node?.config?.features?.allowDirectUpstreamMessages !== false);

  const mySponsors = relationships.filter(r => r.dependentId === member.id);
  const myDependents = relationships.filter(r => r.sponsorId === member.id);

  const getMemberById = (id: string) => allMembers.find(m => m.id === id);

  const isSponsorOfCurrent = mySponsors.some(rel => getMemberById(rel.sponsorId)?.userId === user?.id);

  const handleRoleSave = () => {
    if (onUpdateRole) {
      onUpdateRole(member.id, selectedRole);
    }
    setIsEditingRole(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <motion.div 
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col dark:bg-slate-900"
      >
        <div className="flex items-center justify-between p-4 border-b border-black dark:border-white">
          <h3 className="font-semibold text-gray-900 dark:text-white">{memberTerm} Profile</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Authority Warning for Sponsors */}
          {isSponsorOfCurrent && (
            <div className="p-4 bg-navy-900 text-white rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-2">
              <div className="flex items-center gap-2 text-xs font-bold text-brand-primary-light uppercase tracking-widest">
                <ShieldCheck size={14} /> Authority Scope
              </div>
              <p className="text-[10px] text-blue-100 leading-relaxed">
                You are a registered sponsor for <strong>{member.name}</strong> within {node?.name}. 
                Your authority to manage schedules and fees is <strong>strictly local</strong> to this organization. 
                {member.name} maintains individual autonomy in unrelated clubs or chapters.
              </p>
            </div>
          )}

          {/* Lineage Admin Warning */}
          {perms.isInheritedAdmin && member.nodeId === node?.id && (
            <div className="p-4 bg-amber-50 border-2 border-black dark:border-white border-amber-200 rounded-none space-y-2">
               <div className="flex items-center gap-2 text-xs font-bold text-amber-700 uppercase tracking-widest">
                <ShieldCheck size={14} /> Lineage Oversight
              </div>
              <p className="text-[10px] text-amber-800 leading-relaxed">
                You are viewing this member as a parent administrator. You have full authority to override local roles or restore administrative control if required.
              </p>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col items-center text-center">
            <Avatar 
              userId={member.userId} 
              initials={(member.name || member.email || memberTerm)?.[0] || '?'} 
              imageUrl={member.avatarUrl}
              size="lg" 
              className="w-24 h-24 text-2xl mb-4" 
            />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{member.name || member.email || (member.metadata?.status === 'PENDING' || (member as any).status === 'PENDING' ? 'Pending Invitation' : memberTerm)}</h2>
            <div className="mt-2 flex flex-col items-center gap-3">
              {isEditingRole ? (
                <div className="flex items-center gap-2">
                  <select 
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="text-xs border-2 border-black dark:border-white rounded px-2 py-1 outline-none focus:border-blue-500"
                  >
                    <option value="ADMIN">ADMIN</option>
                    <option value="PRESIDENT">PRESIDENT</option>
                    <option value="CAPTAIN">CAPTAIN</option>
                    <option value="TREASURER">TREASURER</option>
                    <option value="MODERATOR">MODERATOR</option>
                    <option value="MEMBER">MEMBER</option>
                  </select>
                  <Button size="sm" className="h-7 px-2 text-[10px]" onClick={handleRoleSave}>Save</Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => setIsEditingRole(false)}>Cancel</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="blue">{member.role}</Badge>
                  {canManage && !isUpstream && (
                    <button 
                      onClick={() => setIsEditingRole(true)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Change Role"
                    >
                      <Tag size={12} />
                    </button>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                {member.memberType === 'DEPENDENT' && <Badge variant="amber">DEPENDENT</Badge>}
                {member.memberType === 'SPONSOR' && <Badge variant="green">SPONSOR</Badge>}
              </div>
            </div>
          </div>

          {/* Relationships */}
          {(mySponsors.length > 0 || myDependents.length > 0) && (
            <div className="p-4 bg-white rounded-none space-y-4 border-2 border-black dark:border-white dark:bg-slate-950">
               {mySponsors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono flex items-center gap-1.5">
                    <User size={10} /> Sponsoring Guardians
                  </p>
                  <div className="space-y-2">
                    {mySponsors.map(rel => {
                      const sponsor = getMemberById(rel.sponsorId);
                      return sponsor ? (
                        <div key={rel.id} className="flex items-center justify-between bg-white p-2 rounded-none border-2 border-black dark:border-white dark:bg-slate-900">
                          <div className="flex items-center gap-2">
                            <Avatar 
                              userId={sponsor.userId} 
                              initials={sponsor.name?.[0] || sponsor.email?.[0] || '?'} 
                              imageUrl={sponsor.avatarUrl}
                              size="sm" 
                            />
                            <span className="text-xs font-medium">{sponsor.name || sponsor.email || 'Unknown Sponsor'}</span>
                          </div>
                          <Badge variant="gray" className="text-[9px]">{rel.relationshipType}</Badge>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {myDependents.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono flex items-center gap-1.5">
                    <ShieldCheck size={10} /> Dependents Managed
                  </p>
                  <div className="space-y-2">
                    {myDependents.map(rel => {
                      const dependent = getMemberById(rel.dependentId);
                      return dependent ? (
                        <div key={rel.id} className="flex items-center justify-between bg-white p-2 rounded-none border-2 border-black dark:border-white dark:bg-slate-900">
                          <div className="flex items-center gap-2">
                            <Avatar 
                              userId={dependent.userId} 
                              initials={dependent.name?.[0] || dependent.email?.[0] || '?'} 
                              imageUrl={dependent.avatarUrl}
                              size="sm" 
                            />
                            <span className="text-xs font-medium">{dependent.name || dependent.email || 'Unknown Dependent'}</span>
                          </div>
                          <Badge variant="amber" className="text-[9px]">{rel.relationshipType}</Badge>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-black dark:border-white">
                <p className="text-[9px] text-gray-400 leading-relaxed italic">
                  Note: Relationships listed here apply strictly within {node?.name}. Guardians have no implied authority over members in external organizations.
                </p>
              </div>
            </div>
          )}

          {/* Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <MapPin size={16} className="text-gray-400" />
              <span>{member.nodeName}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <Calendar size={16} className="text-gray-400" />
              <span>{memberTerm} since {safeFormat(member.joinedAt, 'MMMM yyyy')}</span>
            </div>
          </div>

          {/* Skills */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Skills & Expertise</p>
            <div className="flex flex-wrap gap-2">
              {(member.skills || []).length > 0 ? (member.skills || []).map(skill => (
                <span key={skill} className="px-2.5 py-1 bg-white text-gray-700 rounded-none text-xs font-medium border-2 border-black dark:border-white dark:bg-slate-800">
                  {skill}
                </span>
              )) : (
                <p className="text-xs text-gray-400 italic">No skills listed.</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          {member.metadata && Object.keys(member.metadata).length > 0 && (
            <div className="space-y-3">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Additional Info</p>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(member.metadata).map(([key, value]) => (
                  <div key={key}>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase">{key.replace('_', ' ')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{String(value)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-black dark:border-white bg-white dark:bg-slate-950">
          {isCurrentUser ? (
            <div className="p-3 bg-gray-50 dark:bg-slate-900 text-gray-500 rounded-none text-xs font-bold uppercase tracking-wider text-center border-2 border-black dark:border-white">
              Your Profile
            </div>
          ) : allowDirectMessage ? (
            <Button variant="primary" className="w-full" isLoading={createDirectChannel.isPending} onClick={() => {
              createDirectChannel.mutate(member.userId, {
                onSuccess: (channel) => {
                  navigate(`/chat?channel=${channel.id}`);
                },
                onError: (err) => {
                  toast.error('Failed to create direct message channel');
                  navigate('/chat');
                }
              });
            }}>
              <MessageSquare size={18} />
              Send Message
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 text-amber-700 rounded-none text-[10px] uppercase font-bold tracking-wider text-center border-2 border-black dark:border-white border-amber-100">
                Direct Messaging Restricted
              </div>
              <Button variant="secondary" className="w-full" onClick={() => window.location.href = '/discussions'}>
                <LayoutList size={18} />
                Post to {member.nodeName} {getTerm('discussions')}
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// --- Main Page ---

const ROLES = ['ADMIN', 'MODERATOR', 'TREASURER', 'EVALUATOR', 'MEMBER', 'OBSERVER'];

export const DirectoryPage = () => {
  const { node, getTerm } = useTenant();
  const { user } = useCurrentUser();
  const [filters, setFilters] = React.useState<DirectoryFilters>({
    name: '',
    role: [],
    skill: '',
    includeDescendants: false
  });
  const [selectedMember, setSelectedMember] = React.useState<DirectoryMember | null>(null);

  const query = useDirectory(node?.id || '', filters);
  const { data, isLoading, error } = query;
  const updateMemberRole = useUpdateMemberRole(node?.id || '');
  const members = Array.isArray(data?.members) ? data?.members : (data?.members as any)?.content || [];
  const nodeCount = data?.nodeCount || 0;
  const memberTerm = getTerm('member');

  const handleUpdateRole = (memberId: string, newRole: string) => {
    const target = members.find((m: any) => m.id === memberId);
    const userId = target?.userId || memberId;
    updateMemberRole.mutate({ userId, role: newRole }, {
      onSuccess: () => {
        if (selectedMember && selectedMember.id === memberId) {
          setSelectedMember({ ...selectedMember, role: newRole });
        }
      }
    });
  };

  const handleSearchChange = React.useMemo(
    () => debounce((val: string) => setFilters(prev => ({ ...prev, name: val })), 300),
    []
  );

  const toggleRole = (role: string) => {
    setFilters(prev => ({
      ...prev,
      role: prev.role?.includes(role) 
        ? prev.role.filter(r => r !== role) 
        : [...(prev.role || []), role]
    }));
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {error && (
        <ErrorBanner 
          title="Directory Sync Error" 
          message="We couldn't search the directory. Please try again."
          onRetry={() => query.refetch()}
        />
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">{memberTerm} Directory</h1>
          <p className="text-xs md:text-sm text-gray-500">Find and connect with {memberTerm}s across the organization.</p>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="space-y-6">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search by name, email, or ID..." 
            className="w-full pl-10 pr-4 py-2 text-sm border-2 border-black dark:border-white rounded-none focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Filter by Role</p>
            <div className="flex flex-wrap gap-2">
              {ROLES.map(role => (
                <button
                  key={role}
                  onClick={() => toggleRole(role)}
                  className={cn(
                    "px-3 py-1.5 rounded-none text-xs font-medium border-2 border-black dark:border-white transition-all",
                    filters.role?.includes(role)
                      ? "bg-navy-900 text-white border-navy-900"
                      : "bg-white text-gray-600 dark:text-gray-300 border-black dark:border-white hover:border-blue-300 hover:text-blue-600 dark:bg-slate-900"
                  )}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-full sm:w-48">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-2">Skill / Tag</p>
              <div className="relative">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="e.g. Design" 
                  className="w-full pl-9 pr-3 py-1.5 text-xs border-2 border-black dark:border-white rounded-none focus:outline-none focus:border-blue-500"
                  value={filters.skill}
                  onChange={(e) => setFilters(prev => ({ ...prev, skill: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 sm:pt-0">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className={cn(
                  "w-10 h-5 rounded-none relative transition-colors",
                  filters.includeDescendants ? "bg-blue-600" : "bg-gray-200"
                )}>
                  <input 
                    type="checkbox" 
                    className="hidden" 
                    checked={filters.includeDescendants}
                    onChange={(e) => setFilters(prev => ({ ...prev, includeDescendants: e.target.checked }))}
                  />
                  <div className={cn(
                    "absolute top-1 left-1 w-3 h-3 bg-white rounded-none transition-transform dark:bg-slate-900",
                    filters.includeDescendants ? "translate-x-5" : "translate-x-0"
                  )} />
                </div>
                <span className="text-xs font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">Include child nodes</span>
              </label>
            </div>
          </div>
        </div>

        {filters.includeDescendants && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 text-blue-700 rounded-none text-xs font-medium animate-in fade-in slide-in-from-top-2 duration-200">
            <Globe size={14} />
            Searching across {nodeCount} nodes in the organization hierarchy.
          </div>
        )}
      </Card>

      {/* Results */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Searching directory...</p>
        </div>
      ) : members.length > 0 ? (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[800px]">
              <thead>
                <tr className="bg-white border-b border-black dark:border-white dark:bg-slate-950">
                  <th className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">{memberTerm}</th>
                  <th className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Skills</th>
                  <th className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.map((member: any) => (
                  <tr 
                    key={member.id} 
                    className="group hover:bg-white transition-colors cursor-pointer dark:bg-slate-950"
                    onClick={() => setSelectedMember(member)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Avatar 
                          userId={member.userId} 
                          initials={member.name?.[0] || member.email?.[0] || '?'} 
                          imageUrl={member.avatarUrl}
                          size="md" 
                          className="flex-shrink-0"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">{member.name || member.email || (member.metadata?.status === 'PENDING' || (member as any).status === 'PENDING' ? 'Pending Invitation' : memberTerm)}</span>
                          <span className="text-[10px] text-gray-400 font-mono truncate">{member.email}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[9px] font-black uppercase text-blue-600 bg-blue-50 px-1 rounded-sm border-2 border-black dark:border-white border-blue-100">{member.role}</span>
                            <span className="text-[9px] text-gray-400 truncate flex items-center gap-1">
                              <MapPin size={8} />
                              {member.nodeName}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1.5">
                        {(member.skills || []).slice(0, 3).map((skill: string) => (
                          <span key={skill} className="px-2 py-0.5 bg-white text-gray-600 dark:text-gray-300 rounded text-[10px] font-medium border-2 border-black dark:border-white dark:bg-slate-800">
                            {skill}
                          </span>
                        ))}
                        {(member.skills || []).length > 3 && (
                          <span className="px-2 py-0.5 bg-white text-gray-400 rounded text-[10px] font-bold border-2 border-black dark:border-white dark:bg-slate-950">
                            +{(member.skills || []).length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {(! (member.nodeId !== node?.id && node?.config?.features?.allowDirectUpstreamMessages === false)) && member.userId !== (user?.id || user?.userId) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMember(member);
                            }}
                          >
                            <MessageSquare size={14} />
                          </Button>
                        )}
                        <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="text-center py-20 bg-white border-2 border-black dark:border-white border-dashed border-black dark:border-white rounded-none dark:bg-slate-900">
          <User size={40} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">No {memberTerm}s found</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Try adjusting your filters or search terms.</p>
        </div>
      )}

      <AnimatePresence>
        {selectedMember && (
          <MemberDrawer 
            member={selectedMember} 
            relationships={data?.relationships || []}
            allMembers={members}
            onClose={() => setSelectedMember(null)} 
            onUpdateRole={handleUpdateRole}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
